/**
 * TanStack Query full-client persistence in IndexedDB (single blob).
 */

'use client';

import type { Persister } from '@tanstack/query-persist-client-core';
import { openDB, type IDBPDatabase } from 'idb';

const DB_NAME = 'nomu-tanstack-persist';
const DB_VERSION = 1;
const STORE = 'cache';
const CLIENT_KEY = 'react-query';

let dbPromise: Promise<IDBPDatabase> | null = null;

async function getDb(): Promise<IDBPDatabase> {
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IndexedDB unavailable'));
  }
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE);
        }
      },
    });
  }
  return dbPromise;
}

/** Removes persisted TanStack cache (e.g. logout). */
export async function clearTanstackPersistStorage(): Promise<void> {
  try {
    const db = await getDb();
    await db.delete(STORE, CLIENT_KEY);
  } catch {
    // ignore
  }
}

export function createTanstackIndexedPersister(): Persister {
  return {
    persistClient: async (persistedClient) => {
      const db = await getDb();
      await db.put(STORE, persistedClient, CLIENT_KEY);
    },
    restoreClient: async () => {
      try {
        const db = await getDb();
        return (await db.get(STORE, CLIENT_KEY)) ?? undefined;
      } catch {
        return undefined;
      }
    },
    removeClient: async () => {
      await clearTanstackPersistStorage();
    },
  };
}
